import { Label } from '@/shad/ui/label'
import { RadioGroup, RadioGroupItem } from '@/shad/ui/radio-group'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import FamilyWizardLayout from '../../FamilyWizardLayout'
import { SubmitButton } from '../../student_details/shared/submit-button'

const FormSchema = z.object({
  taxConsent: z.enum(['yes', 'no'], {
    required_error: 'Please select an option',
  }),
})

type Props = {
  onConsent: () => void
  onNoConsent: () => void
}

type FormData = z.infer<typeof FormSchema>

function TaxCommissionConsentPage(props: Props) {
  const { onConsent, onNoConsent } = props
  const { t } = useTranslation()

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      taxConsent: undefined,
    },
  })

  const onSubmit = (data: FormData) => {
    if (data.taxConsent === 'yes') {
      onConsent()
    } else {
      onNoConsent()
    }
  }

  return (
      <FamilyWizardLayout>
        <FamilyWizardLayout.Header>
          <h1>{t('taxConsent.header')}</h1>
        </FamilyWizardLayout.Header>
        <FamilyWizardLayout.Content>
          <section id="income-verification">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <h2 className="text-gray-800 font-medium text-lg text-left mb-4">
                {t('taxConsent.question')}
              </h2>
              <RadioGroup
                className="space-y-4"
                {...register('taxConsent')}
                onValueChange={(value) => {
                  const values = FormSchema.parse({
                    taxConsent: value,
                  })
                  setValue('taxConsent', values.taxConsent)
                }}
              >
                <div className="flex space-x-2 rounded-lg py-2 px-2 hover:bg-gray-100">
                  <RadioGroupItem
                    value="yes"
                    id="consent-yes"
                  />
                  <Label
                    htmlFor="consent-yes"
                    className="flex flex-col flex-1"
                  >
                  <span
                    className="font-medium mb-1 text-md"
                  >
                    {t('taxConsent.yes.label')}
                  </span>
                  <span className="text-gray-700 font-normal">
                    {t('taxConsent.yes.description')}
                  </span>
                  </Label>
                </div>
                <div className="flex space-x-2 rounded-lg py-2 px-2 hover:bg-gray-100">
                  <RadioGroupItem
                    value="no"
                    id="consent-no"
                  />
                  <Label
                    htmlFor="consent-no"
                    className="flex flex-col flex-1"
                  >
                  <span className="font-medium mb-1 text-md">
                    {t('taxConsent.no.label')}
                  </span>
                    <span className="text-gray-700 font-normal">
                    {t('taxConsent.no.description')}
                  </span>
                  </Label>
                </div>
              </RadioGroup>

              {errors.taxConsent && (
                <p className="text-red-500 text-sm" role="alert">
                  {t('taxConsent.error')}
                </p>
              )}
            </form>
          </section>
        </FamilyWizardLayout.Content>
        <FamilyWizardLayout.ActionBar>
          <SubmitButton
            status="idle"
            buttonTxt={t('taxConsent.continue')}
            onClick={handleSubmit(onSubmit)}
          />
        </FamilyWizardLayout.ActionBar>
      </FamilyWizardLayout>
  )
}

export default TaxCommissionConsentPage
